import type { OutputFormatId } from '../project/output-format.ts';
import { readGoProGps } from '../telemetry/gopro.ts';
import { movieCreationTime, type ByteSource } from '../telemetry/mp4-samples.ts';
import { findLocation, type GeoLocation } from './location.ts';

/**
 * What a video file says about where, when and on what it was made, read from
 * its container tags (docs/seo-plan.md SC-10). Phones and cameras use several
 * keys for each fact, so each is looked up by every key known to carry it, in
 * the order the most specific writers use. Nothing here leaves the device.
 */

/** When a file says it was recorded: the moment, and the UTC offset it was written with, if any. */
export interface RecordedAt {
  readonly at: Date;
  /** "+03:00" when the file says it; null when it only gives UTC or no zone. */
  readonly offset: string | null;
}

export interface Tag {
  readonly key: string;
  readonly value: string;
}

export interface MetadataFindings {
  readonly location: GeoLocation | null;
  readonly make: string | null;
  readonly model: string | null;
  readonly software: string | null;
  readonly recorded: RecordedAt | null;
  /** Every other readable tag, so nothing the file says is hidden. */
  readonly other: readonly Tag[];
}

const MAKE_KEYS = ['com.apple.quicktime.make', '©mak', 'make', 'MAKE', 'com.android.manufacturer'];
const MODEL_KEYS = ['com.apple.quicktime.model', '©mod', 'model', 'MODEL', 'com.android.model'];
/** The phone's system first, then the program that wrote the file. */
const SOFTWARE_KEYS = ['com.apple.quicktime.software', '©swr', 'software', 'com.android.version', '©too', 'encoder', 'ENCODER'];
const RECORDED_KEYS = ['com.apple.quicktime.creationdate', '©day', 'creation_time', 'DATE_RECORDED', 'DATE'];
/** Container bookkeeping that says nothing about the person or the device. */
const TECHNICAL = /^(major_brand|minor_version|compatible_brands|handler_name|vendor_id|duration|language|title-eng)$/i;
/** Longer values are binary or noise, not something to show a person. */
const MAX_VALUE = 160;
const MAX_OTHER = 20;

/** A tag as text, or null when it is binary, empty or not text at all. */
function text(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() || null;
  if (Array.isArray(value)) {
    const parts = value.map(text).filter((part): part is string => part !== null);
    return parts.length ? parts.join(', ') : null;
  }
  return null;
}

const first = (raw: Readonly<Record<string, unknown>>, keys: readonly string[]): { key: string; value: string } | null => {
  for (const key of keys) {
    const value = text(raw[key]);
    if (value) return { key, value };
  }
  return null;
};

/** "2026-05-04T18:21:07+0300", "2026-05-04 18:21:07Z", "2026": a time as files write it, or null. */
export function parseRecorded(value: string): RecordedAt | null {
  const trimmed = value.trim();
  const zone = /([+-])(\d{2}):?(\d{2})$|Z$/i.exec(trimmed);
  const offset = zone?.[1] ? `${zone[1]}${zone[2]}:${zone[3]}` : null;
  const normal = (zone?.[1] ? trimmed.slice(0, zone.index) + offset : trimmed).replace(' ', 'T');
  const bare = /^\d{4}(-\d{2}(-\d{2})?)?$/.test(normal);
  // A bare date or year has no time, and a time with no zone is the wall clock as written: both read as UTC, so the viewer's own zone never moves them.
  const at = bare ? new Date(`${normal}${normal.length === 4 ? '-01-01' : normal.length === 7 ? '-01' : ''}T00:00:00Z`) : new Date(zone ? normal : `${normal}Z`);
  const year = at.getUTCFullYear();
  return Number.isNaN(at.getTime()) || year < 1980 || year > 2200 ? null : { at, offset };
}

/** The wall-clock time the file was recorded at, where it was recorded when the file says so: `{ day: 'YYYY-MM-DD', time: 'HH:MM', offset }`. */
export function wallClock({ at, offset }: RecordedAt): { readonly day: string; readonly time: string; readonly offset: string | null } {
  const minutes = offset ? (offset.startsWith('-') ? -1 : 1) * (Number(offset.slice(1, 3)) * 60 + Number(offset.slice(4, 6))) : 0;
  const local = new Date(at.getTime() + minutes * 60_000).toISOString();
  return { day: local.slice(0, 10), time: local.slice(11, 16), offset };
}

/** The key without the writer's namespace or the QuickTime copyright sign: "com.apple.quicktime.author" → "author". */
const shortKey = (key: string): string => key.replace(/^com\.(apple\.quicktime|android)\./, '').replace(/^©/, '');

/**
 * Reads the findings from a file's raw tags. `date` is the media library's own
 * reading of the recording date; `movieCreated` the MP4 `mvhd` time, which is
 * often the only one Android phones write.
 */
export function readMetadata(raw: Readonly<Record<string, unknown>> = {}, date?: Date | null, movieCreated?: Date | null): MetadataFindings {
  const make = first(raw, MAKE_KEYS);
  const model = first(raw, MODEL_KEYS);
  const software = first(raw, SOFTWARE_KEYS);
  const recordedTag = RECORDED_KEYS.map((key) => ({ key, value: text(raw[key]) })).find((entry) => entry.value && parseRecorded(entry.value));
  const fallback = [date, movieCreated].find((value): value is Date => value instanceof Date && !Number.isNaN(value.getTime()));
  const recorded = recordedTag ? parseRecorded(recordedTag.value!) : fallback ? parseRecorded(fallback.toISOString()) : null;

  const shown = new Set([make?.key, model?.key, software?.key, recordedTag?.key, ...RECORDED_KEYS]);
  const other: Tag[] = [];
  for (const [key, value] of Object.entries(raw)) {
    if (shown.has(key) || TECHNICAL.test(key) || findLocation({ [key]: value })) continue;
    const readable = text(value);
    if (!readable || readable.length > MAX_VALUE) continue;
    if (other.some((tag) => tag.value === readable && shortKey(tag.key) === shortKey(key))) continue;
    other.push({ key: shortKey(key), value: readable });
    if (other.length === MAX_OTHER) break;
  }
  return {
    location: findLocation(raw),
    make: make?.value ?? null,
    model: model?.value ?? null,
    software: software ? (software.key === 'com.android.version' ? `Android ${software.value}` : software.value) : null,
    recorded,
    other,
  };
}

/** "Apple iPhone 15 Pro", without saying the maker twice ("Apple Apple iPhone"). */
export function deviceName({ make, model }: Pick<MetadataFindings, 'make' | 'model'>): string | null {
  if (!make) return model;
  if (!model) return make;
  return model.toLowerCase().startsWith(make.toLowerCase()) ? model : `${make} ${model}`;
}

/** A position shown to about a kilometre (two decimals), with its hemispheres. */
export function roundedPosition({ latitude, longitude }: GeoLocation): { readonly latitude: string; readonly longitude: string; readonly ns: 'N' | 'S'; readonly ew: 'E' | 'W' } {
  return { latitude: Math.abs(latitude).toFixed(2), longitude: Math.abs(longitude).toFixed(2), ns: latitude < 0 ? 'S' : 'N', ew: longitude < 0 ? 'W' : 'E' };
}

/** The format a clean copy is saved in: the original's own container when Shycut writes it, MP4 otherwise. */
export function cleanCopyFormat(mimeType: string): OutputFormatId {
  if (mimeType.includes('quicktime')) return 'mov';
  if (mimeType.includes('webm')) return 'webm';
  return 'mp4';
}

/** Whether the file says anything about the person: where, on what, or when. */
export const revealsAnything = (findings: Pick<MetadataFindings, 'location' | 'make' | 'model' | 'recorded'>, hasTrack = false): boolean =>
  Boolean(findings.location || hasTrack || findings.make || findings.model || findings.recorded);

/** What a whole file says: its tags' findings, a camera's GPS track, and the format a clean copy is saved in. */
export interface FileMetadata extends MetadataFindings {
  /** A camera's GPS track (GoPro telemetry): positions with a fix, and the first of them. */
  readonly track: { readonly points: number; readonly first: GeoLocation } | null;
  readonly cleanFormat: OutputFormatId;
}

/** The little of a media library's input this needs (mediabunny's `Input` fits), so the browser and scripts share one reader. */
export interface TaggedInput {
  getFormat(): Promise<{ readonly mimeType: string }>;
  getMetadataTags(): Promise<{ readonly raw?: Readonly<Record<string, unknown>>; readonly date?: Date }>;
}

/**
 * Reads everything a file says about where, when and on what it was made:
 * the container's tags, the movie header's time and a GoPro GPS track. Only
 * metadata is read, never a frame. A part that can't be read counts as absent.
 */
export async function readFileMetadata(input: TaggedInput, bytes: ByteSource): Promise<FileMetadata> {
  const [format, tags, created, track] = await Promise.all([
    input.getFormat(),
    input.getMetadataTags().catch(() => undefined),
    movieCreationTime(bytes).catch(() => null),
    readGoProGps(bytes).catch(() => null),
  ]);
  const first = track?.[0];
  return {
    ...readMetadata(tags?.raw, tags?.date ?? null, created),
    track: first ? { points: track.length, first: { latitude: first.latitude, longitude: first.longitude } } : null,
    cleanFormat: cleanCopyFormat(format.mimeType),
  };
}
